/**
 * Normalize Firestore Timestamp, seconds, or ms into epoch milliseconds for comparisons.
 */
export function toMillis(v: unknown): number {
  if (v == null || v === '') return 0;
  if (typeof v === 'number' && Number.isFinite(v)) {
    if (v <= 0) return 0;
    // Heuristic: Unix seconds for years 2001–2286 are < 1e10; ms since epoch today ~1.7e12
    if (v < 1e11) return Math.round(v * 1000);
    return Math.round(v);
  }
  if (typeof v === 'object' && v !== null) {
    const any = v as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
    if (typeof any.toMillis === 'function') {
      const m = any.toMillis();
      return Number.isFinite(m) ? m : 0;
    }
    if (typeof any.seconds === 'number') {
      return any.seconds * 1000 + ((any.nanoseconds ?? 0) / 1e6);
    }
  }
  const n = Number(v);
  return Number.isFinite(n) ? toMillis(n) : 0;
}

/** Order placement time in ms (handles Timestamp / number from Firestore). */
export function orderCreatedAtMs(order: { timestamp?: unknown }): number {
  return toMillis(order?.timestamp);
}

/** YYYY-MM-DD for a calendar day in the given IANA timezone (optional day offset). */
export function ymdInTimeZone(timeZone: string, offsetDays = 0): string {
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone });
  return offsetDays === 0 ? todayStr : addDaysToYmd(todayStr, offsetDays);
}

export function addDaysToYmd(ymd: string, days: number): string {
  const [year, month, day] = ymd.split('-').map(Number);
  const ref = new Date(year, month - 1, day);
  ref.setDate(ref.getDate() + days);
  const yy = ref.getFullYear();
  const mm = String(ref.getMonth() + 1).padStart(2, '0');
  const dd = String(ref.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Start/end of a YYYY-MM-DD day in local browser calendar (matches Reports date-range queries). */
export function ymdToStartMs(ymd: string): number {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
}

export function ymdToEndMs(ymd: string): number {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
}

/** Sale/payment time in ms for reporting and POS date filters. */
export function orderSaleAtMs(order: { timestamp?: unknown; payments?: { timestamp?: unknown }[] }): number {
  if (order.payments?.length) {
    return Math.max(...order.payments.map((p) => toMillis(p.timestamp)));
  }
  return orderCreatedAtMs(order);
}

/** Sale date key (YYYY-MM-DD) in business timezone. */
export function orderSaleDateKey(
  order: { timestamp?: unknown; payments?: { timestamp?: unknown }[] },
  timeZone: string,
): string {
  const saleMs = orderSaleAtMs(order);
  return new Date(saleMs).toLocaleDateString('en-CA', { timeZone });
}
